/**
 * lib/permissions.js — Iter 38.5 single source of truth for plan-admin role gating.
 *
 * Per directive:
 *   "Only show create/upload/manage actions to authorized roles.
 *    Examples: owner, admin, executive, project_manager, superintendent,
 *    field_engineer where appropriate."
 *
 * The plan room admin set is deliberately broad — field engineers and
 * supers ALSO need to upload sheets / revisions while on site. Read-only
 * users (viewer, demo) never see admin actions.
 */

const PLAN_ADMIN_ROLES = new Set([
  "owner",
  "admin",
  "executive",
  "project_manager",
  "superintendent",
  "field_engineer",
]);


export function canAdminPlans(user) {
  if (!user || typeof user !== "object") return false;
  return PLAN_ADMIN_ROLES.has(user.role);
}


// Project create is more restrictive — matches backend require_role on
// POST /projects (admin / project_manager / owner / executive).
const PROJECT_CREATE_ROLES = new Set(["owner", "admin", "executive", "project_manager"]);

export function canCreateProject(user) {
  if (!user || typeof user !== "object") return false;
  return PROJECT_CREATE_ROLES.has(user.role);
}

// Iter 63 — Add Pipe is a CAD-style power-user action. Field roles
// (foreman / subcontractor / viewer / inspector / safety_manager) must
// not see it in the Sheet Viewer toolbar.
const PIPE_AUTHOR_ROLES = new Set([
  "owner", "admin", "executive", "project_manager",
  "superintendent", "field_engineer",
]);

export function canAuthorPipes(user) {
  if (!user || typeof user !== "object") return false;
  return PIPE_AUTHOR_ROLES.has(user.role);
}

// Iter 63 P0-4 — Role-aware Object Inspector tabs.
//
// Directive:
//   - Viewer-class roles (viewer / inspector / safety_manager /
//     subcontractor): see Overview, Docs, Photos only.
//   - Foreman: same + Timeline.
//   - PM / Superintendent / Field Engineer / Owner / Admin /
//     Executive: see all 5 tabs (full operational picture).
//
// The IDs match the tabs declared in ObjectInspector / StructureInspector.
const VIEWER_TABS = ["overview", "documents", "photos"];
const FOREMAN_TABS = ["overview", "documents", "photos", "timeline"];
const FULL_TABS = ["overview", "photos", "documents", "revisions", "timeline"];

const VIEWER_CLASS_ROLES = new Set([
  "viewer", "inspector", "safety_manager", "subcontractor",
]);

export function inspectorTabsForRole(user) {
  if (!user || typeof user !== "object") return FULL_TABS;
  if (user.role === "foreman") return FOREMAN_TABS;
  if (VIEWER_CLASS_ROLES.has(user.role)) return VIEWER_TABS;
  return FULL_TABS;
}
